<script setup>
import { computed } from 'vue';
import UiBarListCard from '@/components/ui/UiBarListCard.vue';
import UiPageHeader from '@/components/ui/UiPageHeader.vue';
import UiStatCard from '@/components/ui/UiStatCard.vue';
import ReportsMenu from '@/components/dashboard/ReportsMenu.vue';
import WatchListCard from '@/components/dashboard/WatchListCard.vue';
import StudentRiskDialog from '@/components/dashboard/StudentRiskDialog.vue';
import LardoAlertDialog from '@/components/dashboard/LardoAlertDialog.vue';
import { useAdminDashboard } from '@/composables/useAdminDashboard';
import { useDialogs } from '@/composables/useDialogs';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { refreshNotifications } from '@/composables/useNotifications';

// Admin dashboard: headline numbers, breakdowns by grade level and the
// list of students who need attention (low grades and LARDO alerts).
useSessionGuard();

const {
  schoolYear,
  loading,
  error,
  load,
  levels,
  grades,
  counts,
  atRiskStudents,
  lardoAlerts,
  lardoPending,
  byGrade,
  reports,
  openReport,
} = useAdminDashboard();

// The refresh button reloads the bell too, so a refresh shows the latest
// notifications as well as the latest figures.
function refresh() {
  load();
  refreshNotifications();
}

const RISK = '#ef4444';
const LARDO = '#f59e0b';

const percentOfEnrolled = (n) =>
  counts.value.total ? Math.round((n / counts.value.total) * 100) : 0;

const enrolledDescription = computed(() => {
  if (levels.value.jhs && levels.value.shs) {
    return `${counts.value.jhs} Junior High · ${counts.value.shs} Senior High`;
  }
  return levels.value.jhs ? 'Junior High (Grades 7–10)' : 'Senior High (Grades 11–12)';
});

const dialogs = useDialogs();
const selected = dialogs.selected;
const studentOpen = dialogs.model('student');
const alertOpen = dialogs.model('alert');
</script>

<template>
  <div class="page-stack">
    <UiPageHeader
      title="Dashboard"
      :subtitle="schoolYear ? `School year ${schoolYear}` : 'Overview for the selected school year'"
    >
      <template #actions>
        <v-btn
          variant="text"
          icon="mdi-refresh"
          size="small"
          :loading="loading"
          aria-label="Refresh"
          @click="refresh"
        />
        <ReportsMenu :reports="reports" @open="openReport" />
      </template>
    </UiPageHeader>

    <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>

    <section class="stat-grid" aria-label="Overview">
      <UiStatCard
        title="Enrolled students"
        icon="mdi-account-group-outline"
        :value="counts.total"
        :description="enrolledDescription"
        :loading="loading"
      />
      <UiStatCard
        title="Students at risk"
        icon="mdi-alert-circle-outline"
        :value="counts.atRisk"
        :progress="percentOfEnrolled(counts.atRisk)"
        :tone="RISK"
        :description="`${percentOfEnrolled(counts.atRisk)}% of enrolled have a grade below 80`"
        :loading="loading"
      />
      <UiStatCard
        title="LARDO alerts"
        icon="mdi-file-document-alert-outline"
        :value="counts.lardo"
        :progress="percentOfEnrolled(counts.lardo)"
        :tone="LARDO"
        :description="
          lardoPending
            ? `${lardoPending} still waiting for an action`
            : counts.lardo
              ? 'All alerts have an action recorded'
              : 'No alerts this school year'
        "
        :loading="loading"
      />
    </section>

    <section class="chart-grid">
      <UiBarListCard
        title="Enrollment by grade"
        description="Enrolled students per grade level."
        :rows="byGrade"
        :series="[{ key: 'enrolled', label: 'Enrolled', color: 'var(--brand)' }]"
        :loading="loading"
      />
      <UiBarListCard
        title="Attention by grade"
        description="Students at risk and LARDO alerts per grade level."
        :rows="byGrade"
        :series="[
          { key: 'atRisk', label: 'At risk', color: RISK },
          { key: 'lardo', label: 'LARDO', color: LARDO },
        ]"
        :loading="loading"
        empty-text="No students need attention right now."
      />
    </section>

    <WatchListCard
      :at-risk="atRiskStudents"
      :lardo="lardoAlerts"
      :grades="grades"
      :loading="loading"
      @view-student="dialogs.open('student', $event)"
      @view-alert="dialogs.open('alert', $event)"
    />

    <StudentRiskDialog v-model="studentOpen" :student="selected" />
    <LardoAlertDialog v-model="alertOpen" :alert="selected" />
  </div>
</template>
